/**
 * Web API Client - Singleton instance
 * Provides a single ApiClient instance for all web API requests
 */

import { ApiClient } from '@turn-connect/shared/web';
import type { ApiResponse } from '@turn-connect/shared/web';
import { HttpError } from '@turn-connect/shared';
import { getApiBaseUrl } from '../utils/config';

let _webApiClient: ApiClient | null = null;

/**
 * Get or create the singleton web API client instance
 */
export function getWebApiClient(): ApiClient {
  if (!_webApiClient) {
    _webApiClient = new ApiClient(getApiBaseUrl());
  }
  return _webApiClient;
}

/**
 * Await an ApiResponse and convert it to throw-on-error semantics.
 *
 * The shared ApiClient never throws — it returns {success, data, error,
 * status}. Most web services predate it and expose throwing functions, so
 * this helper preserves their contract while routing the request through
 * the central client (CSRF, 401 auto-logout, traceparent, observability).
 * It throws an HttpError (status, body) whose message is the server's error.
 */
export async function unwrap<T>(pending: Promise<ApiResponse<T>>, fallbackMessage?: string): Promise<T> {
  const res = await pending;
  if (!res.success) {
    // An HttpError is an Error with the same message, plus the status and
    // body — so a caller can tell "not found / not yours" (404/403) from a
    // failure worth reporting, which a bare Error made impossible.
    throw new HttpError(res.status ?? 0, res.error || fallbackMessage || 'Request failed', res.errorBody);
  }
  return res.data as T;
}

// Export singleton instance for convenience
export const webApiClient = getWebApiClient();
